import { LedgerDivisionName } from '@data/ledger';
import type { TradeCondition } from '@models/BookingInquiry';
import type { Country } from '@models/Country';
import { InvoiceDetailsUnitNumType } from '@models/next/Invoice';
import { createStrings } from '@utils/local';

export const invoiceStrings = createStrings({
  ja: {
    invoice: '請求書',
    changeLog: '変更履歴',
    d2d_no: 'D2D番号',
    d2d_charge_no: 'D2D請求番号',
    charge_no: '基幹請求番号',
    house_bl_number: 'House BL Number',
    charge_date: '請求日',
    client_id: '請求先',
    invoice_no: 'INV No.',
    payer_mark: '請求先マーク',
    job_code: '台帳番号',
    charge_pay_day: '支払期限',
    vessel_voyage: '航便名',
    transport_route: '航路',
    ata: '入港日',
    quantity_packages: '荷姿',
    quantity: '個数',
    customer_no: 'お客様管理番号',
    conversion_rate: '換算レート',
    language: '請求書言語',
    no: 'NO.',
    invoiceDetails: '請求名目',
    invoice_name: '請求名目',
    invoice_name_preparation: '名目備考',
    unit_price: '請求単価',
    unit_num: '数量',
    unit_num_type: '単位',
    excise_tax: '消費税',
    tax_excluded_price: '非課税額',
    total_price: '請求金額',
    invoice_status: '作成状況',
    payment_status: '支払状況',
    currency: '通貨',
    excise_tax_type: '消費税種類',
    preferred_arrival_date: '納品日',
    payment_date: '入金日',
    finish_date: '消込日',
    salesperson_japan: 'CS(日本)',
    member_salesperson: '営業担当',
    subsums: {
      tax_excluded_total_price: '税抜請求総額',
      excise_tax: '未収消費税',
      tatekae_total_price: '請求立替金額',
    },
    errorClientIdNotSelected: '検索する会社名を入力し、リストから請求先を選んでください。',
    errorNeedsDeliveredStatus: 'お届け完了前に請求書を全部発行したことにできません。',
    errorNonSalespersonJPEditJP: 'CS担当者（日本）のみは日本名目を変更できます。',
    errorSalespersonEditCountryNone: 'この名目は編集不可です。',
    errorInvoiceDetailsLocked:
      '名目はロックされましたから、編集できません。ロックボタンを押して解除してください。',
    errorInvoiceDetailsUsed: '名目は請求書に入っていましたから、編集できません。',
    errorInvoiceDetailsWithInvoiceUnlockAdminOnly: '請求書に入った名目のロック解除はAdminのみです',
    errorNeedLockCNInvoiceDetails: (invoiceNames: string[]) => {
      return `${invoiceNames
        .map((d) => `「${d}」`)
        .join(
          '、',
        )}はロックされていないため、選択できません。請求書の中国名目は、ロックする必要があります。`;
    },
    errorClientCodeEmpty: '正常に請求先の会員番号を取得できませんでした。',
    errorPreAlertNoEta:
      'この案件のETAが確認されていません。「換算レート」を確定するため、PreAlertを作成してETAを確認してください。',
    errorInvoiceDone:
      '請求書全部発行完了の場合は、作成・変更できません。誤った場合は、ITに連絡してください。',
    errorAlreadyInvoiceDone: '既に請求書全部発行完了になりました。',
    errorNotFCJapan: 'FC Japan のみは請求書を追加・更新できます。',
    errorCannotEditInvoice: 'FC Japanは自分が作成した請求書のみを変更できます。',
    errorPreAlertLedgerLocked:
      '案件のロックが施錠されています。そのため、請求書と請求明細の変更・追加・解錠は行えません。解錠が必要な場合は、adminにご連絡ください。',
  },
});

export const tradeCondtionTagStrings = createStrings({
  ja: {
    CHINA: '中国国内料金',
    D2D: 'D2D料金',
  },
});

export const allUnitNumTypes = ['B/L', 'JOB', 'M3'] as const;

export const allExciseTaxTypes = ['', '非課税', '課税8%', '課税10%'] as const;

export const exciseTaxTypeToRate = {
  '': 0,
  非課税: 0,
  '課税8%': 0.08,
  '課税10%': 0.1,
};

export interface TradeConditionItem {
  name: string;
  needsPickup?: boolean;
  tag: 'CHINA' | 'D2D' | 'TAX' | 'CUSTOMS';
}

export type TradeConditionItemTag = 'CHINA' | 'D2D' | 'TAX' | 'CUSTOMS';

export const tradeConditionItems: TradeConditionItem[] = [
  {
    name: '中国国内集荷サービス料',
    tag: 'CHINA',
    needsPickup: true,
  },
  {
    name: '中国輸出通関料',
    tag: 'CHINA',
  },
  {
    name: '中国AHR',
    tag: 'CHINA',
  },
  {
    name: '中国CFSチャージ',
    tag: 'CHINA',
  },
  {
    name: '中国倉庫入庫料',
    tag: 'CHINA',
  },
  {
    name: '海上輸送料金',
    tag: 'D2D',
  },
  {
    name: '日本輸入通関料金',
    tag: 'D2D',
  },
  {
    name: '日本国内配送料金',
    tag: 'D2D',
  },
  {
    name: '日本の輸入関税',
    tag: 'TAX',
  },
  {
    name: '輸入消費税',
    tag: 'TAX',
  },
  {
    name: '税関検査料金 ※発生時のみ',
    tag: 'CUSTOMS',
  },
];

export const tradeConditionTestQuestions: TradeConditionItemTag[] = [
  'CHINA',
  'D2D',
  'TAX',
  'CUSTOMS',
];

export type TradeConditionTestAnswser = Country;

export interface TradeConditionTestResult {
  answers: TradeConditionTestAnswser[];
  result: TradeCondition;
}

export const tradeConditionTestResults: TradeConditionTestResult[] = [
  {
    answers: ['jp'],
    result: 'EXW',
  },
  {
    answers: ['cn', 'jp'],
    result: 'FCA',
  },
  {
    answers: ['cn', 'cn', 'jp', 'cn'],
    result: 'DDU_CHINA',
  },
  {
    answers: ['cn', 'cn', 'jp', 'jp'],
    result: 'DDU',
  },
  {
    answers: ['cn', 'cn', 'cn'],
    result: 'DDP',
  },
];

export const tradeConditionLabelToTags: Record<
  TradeCondition,
  Record<Country, TradeConditionItemTag[]>
> = {
  EXW: {
    cn: [],
    jp: ['CHINA', 'D2D', 'CUSTOMS', 'TAX'],
  },
  FCA: {
    cn: ['CHINA'],
    jp: ['D2D', 'CUSTOMS', 'TAX'],
  },
  DDU: {
    cn: ['CHINA', 'D2D'],
    jp: ['CUSTOMS', 'TAX'],
  },
  DDU_CHINA: {
    cn: ['CHINA', 'D2D', 'CUSTOMS'],
    jp: ['TAX'],
  },
  DDP: {
    cn: ['CHINA', 'D2D', 'CUSTOMS', 'TAX'],
    jp: [],
  },
};

export interface InvoiceDetailsMeta {
  name: string;
  en: string;
  expense_cost: number;
  unit_num_type: InvoiceDetailsUnitNumType;
  division: LedgerDivisionName;
}

export const invoiceDetailsMetaList: InvoiceDetailsMeta[] = [
  {
    name: '関税',
    en: 'CUSTOMS DUTY',
    expense_cost: 72,
    unit_num_type: 'B/L',
    division: 'A',
  },
  {
    name: '輸入消費税',
    en: 'IMPORT CONSUMPTION TAX',
    expense_cost: 73,
    unit_num_type: 'B/L',
    division: 'A',
  },
  {
    name: '地方消費税',
    en: 'LOCAL CONSUMPTION TAX',
    expense_cost: 74,
    unit_num_type: 'B/L',
    division: 'A',
  },
  {
    name: 'D2D料金（非課税分）',
    en: 'D2D NON TAXABLE CHARGE',
    expense_cost: 213,
    unit_num_type: 'JOB',
    division: 'B',
  },
  {
    name: 'D2D料金（課税分）',
    en: 'D2D TAXABLE CHARGE',
    expense_cost: 214,
    unit_num_type: 'JOB',
    division: 'B',
  },
  {
    name: '保険料',
    en: 'INSURANCE',
    expense_cost: 52,
    unit_num_type: 'JOB',
    division: 'B',
  },
  {
    name: '税関検査料',
    en: 'CUSTOMS INSPECTION FEE',
    expense_cost: 13,
    unit_num_type: 'JOB',
    division: 'B',
  },
  {
    name: '中国輸出通関料',
    en: 'EXPORT CUSTOMS CLEARANCE FEE IN CHINA',
    expense_cost: 217,
    unit_num_type: 'B/L',
    division: 'B',
  },
  {
    name: '中国AHRチャージ',
    en: 'AHR CHARGE',
    expense_cost: 218,
    unit_num_type: 'B/L',
    division: 'B',
  },
  {
    name: '中国CFSチャージ',
    en: 'CFS CHARGE CHINA',
    expense_cost: 223,
    unit_num_type: 'M3',
    division: 'B',
  },
  {
    name: '中国倉庫入庫料',
    en: 'CHINA WAREHOUSE GATE CHARGE',
    expense_cost: 219,
    unit_num_type: 'B/L',
    division: 'B',
  },
  {
    name: '中国集荷料金',
    en: 'CHINA PICK UP CHARGE',
    expense_cost: 220,
    unit_num_type: 'JOB',
    division: 'B',
  },
  {
    name: 'クレジットカード手数料',
    en: 'CREDIT CARD FEE',
    expense_cost: 222,
    unit_num_type: 'JOB',
    division: 'B',
  },
  {
    name: '中国貿易代行サービス料',
    en: 'TRADING FEE',
    expense_cost: 221,
    unit_num_type: 'B/L',
    division: 'B',
  },
  {
    name: 'その他料金',
    en: 'OTHERS CHARGE',
    expense_cost: 65,
    unit_num_type: 'JOB',
    division: 'B',
  },
  {
    name: 'SECURITY SURCHARGE',
    en: 'SECURITY SURCHARGE',
    expense_cost: 173,
    unit_num_type: 'B/L',
    division: 'B',
  },
];
